import { isReactive, ref, toValue, watch } from 'vue'
import { storeToRefs } from 'pinia'
import qs from 'query-string'
import { useUserStore } from '@/stores/user'
import { useLockStore } from '@/stores/lock'
import { useConfigsStore } from '@/stores/configs'
import { i18n } from '@/i18n'
import config from '@/config'

/**
 * @typedef {Object} UseApiResult
 * @property {import('vue').Ref} model
 * @property {import('vue').Ref} error
 * @property {import('vue').Ref} pending
 * @property {Function} abort
 * @property {Function: Promise} refresh
 *
 * @typedef {Object} UseApiConfig
 * @property {String|import('vue').Ref} url
 * @property {String} method http method
 * @property {any} data initial value for result model ref; no usable if model given
 * @property {import('vue').Ref} model model that will be set after response
 * @property {Function} transform function for transform request body
 * @property {Function} pick function for transform response body
 * @property {Object|import('vue').Ref} query object to transform to query string
 * @property {Array<import('vue').Ref>|import('vue').Ref} watch when ref(s) changed, request will fetch again
 * @property {Boolean} upload if true, request will be multipart/form-data & data used for body
 * @property {Boolean} immediate if true, will fire request on mount
 * @property {Function} before prevent request if returns false
 * @property {String|import('vue').Ref} baseUrl explicit API base URL override
 */

const BACKEND = config.VITE_API_URL

const httpStatusesNoBody = [201, 202, 204]

/**
 * Переводит английский statusText / описание ошибки по текущей локали.
 *
 * @param {string|null|undefined} message текст ошибки
 * @param {number|null} [status] HTTP-код
 * @returns {string}
 */
const translateErrorMessage = (message, status = null) => {
  const normalizedMessage = String(message ?? '').trim()
  const statusLabel =
    status != null && i18n.global.te(`http.status.${status}`)
      ? i18n.global.t(`http.status.${status}`)
      : ''

  if (!normalizedMessage) {
    return statusLabel || normalizedMessage
  }

  if (/[А-Яа-яЁё]/.test(normalizedMessage)) {
    return normalizedMessage
  }

  const httpErrors = i18n.global.tm('http.errors')
  if (httpErrors && typeof httpErrors === 'object') {
    if (normalizedMessage in httpErrors) {
      return httpErrors[normalizedMessage]
    }
    const lower = normalizedMessage.toLowerCase()
    const matchedKey = Object.keys(httpErrors).find(k => k.toLowerCase() === lower)
    if (matchedKey) {
      return httpErrors[matchedKey]
    }
  }

  return statusLabel || normalizedMessage
}

const mapUri2Entity = new Map([
  [
    /^catalog\/(.+)\/(\d+)$/i,
    (uri, re) => {
      // eslint-disable-next-line no-unused-vars
      const [_, m, i] = re.exec(uri)
      return `${m}/${i}`
    },
  ],
  [
    /^stock\/(.+)\/(\d+)$/i,
    (uri, re) => {
      // eslint-disable-next-line no-unused-vars
      const [_, m, i] = re.exec(uri)
      return `${m}_stock/${i}`
    },
  ],
  [
    /^coordinator\/(.+)\/(\d+)$/i,
    (uri, re) => {
      // eslint-disable-next-line no-unused-vars
      const [_, m, i] = re.exec(uri)
      return `${m}/${i}`
    },
  ],
])

function useUpload(url, files) {
  const _url = `${BACKEND}${url}`
  const fd = new FormData()
  let i = 0
  for (let f of files) {
    fd.append(`file_${i}`, f)
    i++
  }
  return fetch(_url, {
    credentials: 'include',
    cache: 'no-cache',
    method: 'post',
    body: fd,
  })
}

export const downloadLink = (file, baseUrl = BACKEND) =>
  `${toValue(baseUrl) || BACKEND}download/${file.url}`

/**
 * @param {UseApiConfig} cfg
 * @returns {UseApiResult}
 */
const useApi = cfg => {
  const initialCfg = { ...cfg }
  const model = initialCfg.model ? initialCfg.model : ref(initialCfg.data || null)
  const immediate = initialCfg.immediate === true || false
  const transform = initialCfg.transform ? initialCfg.transform : d => d
  const pick = initialCfg.pick ? initialCfg.pick : d => d
  const isUpload = initialCfg.upload ?? false
  const watchSources = initialCfg.watch ? initialCfg.watch : []
  const error = ref(null)
  const pending = ref(false)
  const ac = ref(null)
  const status = ref(null)
  const { user, isAuthorized } = storeToRefs(useUserStore())
  const { isApiOk } = storeToRefs(useConfigsStore())

  const { lockKeys: lkk } = storeToRefs(useLockStore())

  if (initialCfg.data) {
    delete initialCfg.data
  }

  const refresh = async (_cfg = null) => {
    status.value = null
    let cfg = { ...initialCfg }
    if (_cfg !== null) {
      Object.assign(cfg, _cfg)
    }

    if (cfg.before instanceof Function) {
      if (cfg.before() === false) {
        return
      }
    }

    const baseUrl = determineBaseUrl(cfg)
    const url = new URL(toValue(cfg.url), baseUrl)

    const headers = {
      Accept: 'application/json',
    }

    if (!isUpload) {
      headers['Content-Type'] = 'application/json'
    }
    const lk = getLockKey(url)
    if (lk) {
      headers['X-Api-Lock'] = lk
    }

    let requestBody = null
    let responseBody = null
    let response

    if (isAuthorized.value) {
      headers.Authorization = `Bearer ${user.value.access_token}`
    }

    if (cfg.headers) {
      Object.assign(headers, cfg.headers)
    }

    if (isUpload) {
      // FormData instance
      requestBody = cfg.data
      cfg.method = 'post'
    } else {
      if (_cfg?.data) {
        requestBody = _cfg.data
      } else if (
        model.value !== null &&
        ['post', 'put', 'delete'].includes(String(cfg.method).toLowerCase())
      ) {
        requestBody = toValue(model)
      } else if (cfg.data) {
        requestBody = cfg.data
      }
    }

    if (
      !isUpload &&
      requestBody &&
      ['post', 'put'].includes(String(cfg.method).toLowerCase())
    ) {
      requestBody = stripClientOnlyFields(requestBody)
    }

    if (!isUpload && requestBody) {
      requestBody = JSON.stringify(transform(requestBody))
    }

    if (cfg.query) {
      url.search = qs.stringify(toValue(cfg.query))

      if (cfg.query.value?.filter) {
        const params = new URLSearchParams(url.search)
        const queryParts = []
        params.forEach((value, key) => {
          const values = value.split('&&')
          values.forEach(val => {
            queryParts.push(`${key}=${encodeURIComponent(val)}`)
          })
        })
        url.search = `?${queryParts.join('&')}`
      }
    }

    pending.value = true
    ac.value = new AbortController()

    const fconfig = {
      headers,
      method: cfg.method || 'get',
      cache: 'no-cache',
      body: requestBody,
      signal: ac.value.signal,
    }

    if (cfg.credentials !== false) {
      fconfig.credentials = 'include'
    }

    try {
      response = await fetch(url, fconfig)
      ac.value = null
      status.value = response.status
      if (!response.ok) {
        try {
          responseBody = await response.json()
        } catch (_) {
          responseBody = {}
        }
        const rawDescription =
          responseBody.error_description || responseBody.message ||
          (typeof responseBody.error === 'string' ? responseBody.error : response.statusText)
        const statusTextRu = translateErrorMessage(response.statusText, response.status)
        let description = translateErrorMessage(rawDescription, response.status)
        if ([401, 400, 403, 413, 423].includes(response.status)) {
          let text = response.status
          if (String(rawDescription).toUpperCase() == 'BAD REQUEST') {
            description = ''
          }
          let code = (typeof responseBody.error === 'number' ? responseBody.error : null) ?? response.status
          if (response.status == 413) {
            text = response.statusText
          }
          // костыль, чтобы выкидывать юзера если current_user отдал 401
          if (url.pathname.includes("current_user") && response.status === 401) {
            user.value = null
          }
          throw new ApiError(code, description, response.status)
        }
        if (response.status === 403 || response.status === 422) {
          const errMsg =
            responseBody.error_description ??
            responseBody.message ??
            response.statusText
          throw new ApiError(responseBody.error ?? response.status, errMsg, response.status)
        }
        if (isApiOk) isApiOk.value = false
        if (!description) {
          error.value = statusTextRu

          // Ошибка по умолчанию
          const err = new Error(`${statusTextRu} (${response.status})`)
          err.status = response.status
          err.code = response.status
          throw err
        } else {
          error.value = description
          throw new ApiError(responseBody.error ?? response.status, description, response.status)
        }
      }
      if (!httpStatusesNoBody.includes(response.status)) {
        responseBody = await response.json()
      }
    } catch (e) {
      if (e == 'watch source changed') {
        return
      }
      throw e
    } finally {
      pending.value = false
    }

    if (isApiOk) {
      isApiOk.value = true
    }

    if (!response.ok) {
      error.value =
        responseBody.response?.data ||
        responseBody.message ||
        responseBody.error_description ||
        response.statusText
      throw new ApiError(responseBody.error, error.value, response.status)
    }

    if (httpStatusesNoBody.includes(response.status)) {
      return null
    }

    let m = pick(responseBody)
    if (typeof m == 'object' && m !== null) m._loaded = true

    if (isReactive(model)) {
      Object.assign(model, m)
    } else {
      model.value = m
    }

    if (cfg.after instanceof Function) {
      cfg.after(responseBody)
    }
  }

  if (watchSources && watch.length) {
    watch(
      watchSources,
      () => {
        abort('watch source changed')
        refresh()
      },
      { deep: true }
    )
  }

  function abort(reason) {
    if (!ac.value) {
      return
    }
    ac.value.abort(reason)
  }

  function determineBaseUrl(cfg) {
    // Ref(null)/undefined не должен перебивать обычный роутинг base URL
    const explicitBase = cfg.baseUrl != null ? toValue(cfg.baseUrl) : null
    if (explicitBase) {
      return explicitBase
    }

    const urlPath = toValue(cfg.url)
    const method = String(cfg.method).toLowerCase()
    const queryEntity = toValue(cfg.query)?.entity_name
    const modelEntity = toValue(model)?.entity_name

    const ssoPaths = [
      /^\/web\/(login|autologin)$/,
      /^(login|login\/change_password|login\/verify_totp|2fa\/(?:setup|verify-setup|cancel-setup|disable)|logout|current_user|sso_redirect|roles(?:\/\d+)?|users(?:\/\d+(?:\/(?:history|age_password_test))?)?|users_list|user_services|users_updating(?:\/(?:request|history|yearly_test|inactive_no_prid_weekly_test))?)$/,
    ]

    if (ssoPaths.some(pattern => pattern.test(urlPath))) {
      return config.VITE_SSO_URL
    }

    const catalogsPaths = [
      /^catalog\/archive-catalog$/,
      /^catalog\/(medicines(?:\/\d+(?:\/history)?)?)$/,
      /^catalog\/(medicine_trademarks(?:\/\d+(?:\/history)?)?)$/,
      /^catalog\/(medicine_trademarks(?:\/\d+(?:\/merge\/\d+)?)?)$/,
      /^catalog\/(manufacturers(?:\/\d+(?:\/history)?)?)$/,
      /^snapshot\/(MedicineTrademarks|Medicines|Manufacturers)\//,
    ]
    if (catalogsPaths.some(pattern => pattern.test(urlPath))) {
      return config.VITE_CATALOGS_URL
    }

    const catalogEntities = ['medicines', 'medicine_trademarks', 'manufacturers']

    const configPaths = [/^config(?:\/\d+)?$/]
    if (configPaths.some(pattern => pattern.test(urlPath))) {
      if (['put', 'post', 'delete'].includes(method) && catalogEntities.includes(modelEntity)) {
        return config.VITE_CATALOGS_URL
      }
    }

    const lockPaths = [/^(lock|lock_check|lock_reset)$/]
    if (lockPaths.some(pattern => pattern.test(urlPath))) {
      if (catalogEntities.includes(queryEntity)) {
        return config.VITE_CATALOGS_URL
      }
      if (queryEntity === 'tickets') {
        return config.VITE_TICKETS_URL || BACKEND
      }
    }

    const docparsePaths = [/^(convert)/]
    if (docparsePaths.some(pattern => pattern.test(urlPath))) {
      return config.VITE_DOCPARSER_URL
    }

    const ticketsPaths = [/^tickets(?:\/|$)/]
    if (ticketsPaths.some(pattern => pattern.test(urlPath))) {
      return config.VITE_TICKETS_URL || BACKEND
    }

    return BACKEND
  }

  /**
   * @param {URL} url
   */
  function getLockKey(url) {
    const be = new URL(BACKEND)
    const uri = url.pathname.replace(be.pathname, '')
    let kval = lkk?.value[uri]

    if (kval !== undefined) {
      return kval
    }

    for (const [lre, ltr] of mapUri2Entity) {
      if (!lre.test(uri)) {
        continue
      }
      const entity = ltr(uri, lre)
      kval = lkk.value[entity]
      if (!kval) {
        continue
      }
      break
    }

    return kval
  }

  if (immediate) {
    refresh()
  }

  return { model, error, pending, refresh, abort, status }
}

function stripClientOnlyFields(obj) {
  if (obj == null || typeof obj !== 'object') return obj
  const { _loaded, ...rest } = obj
  return rest
}

/**
 * default API request on /someurl/:id
 * @param {string} url
 * @param {ref} model
 * @param {Function} refresh executuion function returned by useApi
 * @returns {Function} function that takes `method` as http method; if none given model will be saved (post - create/ put - update)
 */
const useApiResource =
  (url, model, refresh) =>
  (method = null) => {
    let cfg = {
      url,
    }

    const body = stripClientOnlyFields(toValue(model))
    let _url = toValue(cfg.url)

    if (body.id) {
      _url += `/${body.id}`
    }
    //saving
    if (method === null) {
      cfg = {
        ...cfg,
        data: body,
        method: body.id ? 'put' : 'post',
      }
    } else if (method === 'delete') {
      cfg = {
        ...cfg,
        data: body,
        method: method,
      }
    } else {
      cfg.method = method
    }
    cfg.url = _url
    // cfg.immediate = false

    return refresh(cfg)
  }

class ApiError extends Error {
  constructor(code, message, status) {
    super(message)
    this.name = 'ApiError'
    // error code from backend
    this.code = code ?? 500
    // http status
    this.status = status
  }
}

export { useApi, useApiResource, useUpload, ApiError }
